import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../common/AuthContext";
import {
  connectBrokerAccount,
  deleteBrokerAccount,
  disconnectBrokerAccount,
  getBrokerAccount,
  getBrokerAccounts,
  getBrokerId,
} from "../../../service/api/BrokerAccountApi";
import BrokerLogo from "./BrokerLogo";
import BrokerPageHeader from "./BrokerPageHeader";
import "./Brokers.css";

const maskClientId = (value = "") =>
  value.length > 4 ? `${value.slice(0, 4)}${"*".repeat(Math.min(value.length - 4, 6))}` : value;

const normalizeStatus = (status = "") =>
  status.toUpperCase().replace(/[\s_-]/g, "");

const isPendingVerification = (broker) =>
  [
    "",
    "PENDINGVERIFICATION",
    "DISCONNECTED",
    "FAILED",
    "VERIFICATIONFAILED",
    "CONNECTIONFAILED",
    "AUTHENTICATIONFAILED",
    "TOKENEXPIRED",
  ].includes(
    normalizeStatus(broker.status)
  );

const isConnected = (broker) =>
  ["SUCCESS", "CONNECTED", "ACTIVE", "VERIFIED"].includes(
    normalizeStatus(broker.status)
  );

const isFailedStatus = (broker) =>
  ["FAILED", "VERIFICATIONFAILED", "CONNECTIONFAILED", "AUTHENTICATIONFAILED"]
    .includes(normalizeStatus(broker.status));

const getBrokerType = (broker) =>
  broker.brokerType === "DHAN" ? "dhan" : broker.brokerType === "FYERS" ? "fyers" : "angel";

const getBrokerName = (broker) =>
  broker.brokerType === "DHAN" ? "Dhan" : broker.brokerType === "FYERS" ? "Fyers" : "Angel One";

const BrokersList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionBrokerId, setActionBrokerId] = useState(null);

  useEffect(() => {
    let active = true;

    const loadBrokers = async () => {
      if (!user?.userId) {
        setError("Your user session is missing. Please log in again.");
        setLoading(false);
        return;
      }
      try {
        const data = await getBrokerAccounts(user.userId);
        if (active) setBrokers(Array.isArray(data) ? data : []);
      } catch (requestError) {
        if (active) {
          setError(requestError.response?.data?.message || requestError.message || "Unable to load brokers.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadBrokers();
    return () => { active = false; };
  }, [user?.userId]);

  const deleteBroker = async (broker) => {
    if (!window.confirm(`Remove ${broker.accountName}?`)) return;
    try {
      await deleteBrokerAccount(getBrokerId(broker), user.userId);
      setBrokers((current) => current.filter((item) => getBrokerId(item) !== getBrokerId(broker)));
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || "Unable to remove broker.");
    }
  };

  const refreshBroker = async (broker) => {
    try {
      const refreshed = await getBrokerAccount(getBrokerId(broker), user.userId);
      setBrokers((current) =>
        current.map((item) => getBrokerId(item) === getBrokerId(broker) ? refreshed : item)
      );
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || "Unable to refresh broker.");
    }
  };

  const changeConnection = async (broker, connect) => {
    const brokerId = getBrokerId(broker);
    setActionBrokerId(brokerId);
    setError("");
    try {
      let updatedBroker;
      if (connect) {
        updatedBroker = await connectBrokerAccount(brokerId, user.userId);
      } else {
        updatedBroker = await disconnectBrokerAccount(brokerId, user.userId);
      }

      setBrokers((current) =>
        current.map((item) =>
          getBrokerId(item) === brokerId
            ? {
                ...item,
                ...(updatedBroker || {}),
                status:
                  updatedBroker?.status ||
                  (connect ? "SUCCESS" : "DISCONNECTED"),
              }
            : item
        )
      );
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
        requestError.message ||
        `Unable to ${connect ? "connect" : "disconnect"} broker.`
      );
    } finally {
      setActionBrokerId(null);
    }
  };

  return (
    <main className="broker-page">
      <BrokerPageHeader
        title="My Brokers"
        subtitle="Manage your connected broker accounts"
        action={
          <button className="broker-primary-button" onClick={() => navigate("/brokers/add")}>
            <i className="fa fa-plus" aria-hidden="true"></i>
            Add Broker
          </button>
        }
      />

      <section className="broker-card">
        {loading ? (
          <div className="broker-empty"><i className="fa fa-spinner fa-spin fa-2x"></i><p>Loading broker accounts...</p></div>
        ) : error ? (
          <div className="broker-empty"><div className="broker-api-error">{error}</div></div>
        ) : brokers.length === 0 ? (
          <div className="broker-empty">
            <div className="broker-empty__icon">
              <i className="fa fa-briefcase" aria-hidden="true"></i>
            </div>
            <h2>No brokers connected</h2>
            <p>Add your Dhan or Fyers account to start algo trading.</p>
            <button className="broker-primary-button" onClick={() => navigate("/brokers/add")}>
              Add Your First Broker
            </button>
          </div>
        ) : (
          <div className="broker-table-wrap">
            <table className="broker-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Account Name</th>
                  <th>Broker</th>
                  <th>Client ID / Code</th>
                  <th>Status</th>
                  <th>Connected</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {brokers.map((broker, index) => (
                  <tr key={getBrokerId(broker)}>
                    <td>{index + 1}</td>
                    <td className="broker-table__account">{broker.accountName}</td>
                    <td>
                      <div className="broker-table__broker">
                        <BrokerLogo type={getBrokerType(broker)} />
                        {getBrokerName(broker)}
                      </div>
                    </td>
                    <td>{maskClientId(broker.clientId || broker.clientCode)}</td>
                    <td><span className={`broker-status ${isFailedStatus(broker) ? "broker-status--failed" : ""}`}>{broker.status || (broker.active ? "Active" : "Inactive")}</span></td>
                    <td>
                      <div className="broker-actions">
                        {isPendingVerification(broker) && (
                          <button
                            className="broker-actions__connect"
                            title="Connect and verify broker"
                            disabled={actionBrokerId === getBrokerId(broker)}
                            onClick={() => changeConnection(broker, true)}
                          >
                            <i className={`fa ${actionBrokerId === getBrokerId(broker) ? "fa-spinner fa-spin" : "fa-plug"}`}></i>
                            <span>{actionBrokerId === getBrokerId(broker) ? "Connecting..." : "Connect"}</span>
                          </button>
                        )}
                        {isConnected(broker) && (
                          <button
                            className="broker-actions__disconnect"
                            title="Disconnect broker"
                            disabled={actionBrokerId === getBrokerId(broker)}
                            onClick={() => changeConnection(broker, false)}
                          >
                            <i className={`fa ${actionBrokerId === getBrokerId(broker) ? "fa-spinner fa-spin" : "fa-unlink"}`}></i>
                            <span>{actionBrokerId === getBrokerId(broker) ? "Disconnecting..." : "Disconnect"}</span>
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="broker-actions">
                        <button title="View broker" onClick={() => navigate(`/brokers/${getBrokerId(broker)}`)}>
                          <i className="fa fa-eye"></i>
                        </button>
                        <button title="Refresh broker" onClick={() => refreshBroker(broker)}>
                          <i className="fa fa-refresh"></i>
                        </button>
                        <button className="broker-actions__delete" title="Remove broker" onClick={() => deleteBroker(broker)}>
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default BrokersList;
